import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { startAuthentication } from '@simplewebauthn/browser';
import { Check, X } from 'lucide-react';
import { toast } from 'sonner';

// import IconGoogle from '../icons/login-google.svg?react';
// import IconApple from '../icons/login-apple.svg?react';
// import IconMicrosoft from '../icons/login-microsoft.svg?react';
import { Button } from 'components/Button';
import { unreachable } from 'shared/util';
import { TextField } from 'components/FormComponents';
import { FormProvider, useForm } from 'react-hook-form';
import { LoginUserRecordSchema, type LoginUserRecord, type UserProfile } from 'shared/modelTypes/user';
import { zodResolver } from '@hookform/resolvers/zod';
import { joinPath, usePathContext } from 'engine/pathContext';
import { Heading } from 'components/Heading';
import { router } from 'src/main';
import { initSocket, useToken } from 'engine/api';

export const Route = createFileRoute('/login')({
	component: RouteComponent,
});

function RouteComponent() {
	const { setToken } = useToken();

	const [form, setForm] = useState({ email: '', password: '' });
	const [provider, setProvider] = useState<'passkeys' | 'password' | null>(null);

	const methods = useForm<LoginUserRecord>({
		resolver: zodResolver(LoginUserRecordSchema),
	});

	const { watch } = methods;
	const formPath = usePathContext('');

	const email = watch(joinPath(formPath, 'email') as keyof LoginUserRecord);
	const password = watch(joinPath(formPath, 'password') as keyof LoginUserRecord);

	useEffect(() => {
		setForm({
			email: email ?? '',
			password: password ?? '',
		});
	}, [email, password]);

	// console.log('Websocket url:', import.meta.env.VITE_WEBSOCKET_URL);
	// console.log('Base url:', import.meta.env.VITE_BASE_URL);

	const afterLogin = (profile: UserProfile | undefined) => {
		console.log('afterLogin', profile);

		if (!profile) {
			toast('Login failed', { icon: <X /> });
			return;
		}

		setToken(profile);
		initSocket();
		toast('Login successful', { icon: <Check /> });
		router.navigate({ to: '/dashboard' });
	};

	const determineLoginProvider = async () => {
		try {
			const providerRes = await fetch(`${import.meta.env.VITE_WEBSOCKET_URL}/login-provider-for-email?email=${form.email}`);
			const providerData = (await providerRes.json()) as { provider: 'passkeys' | 'password'; error?: string };

			if (providerData.error) {
				toast(providerData.error, { icon: <X /> });
				return;
			}

			setProvider(providerData.provider);

			// If passkeys, start passkey authentication
			if (providerData.provider === 'passkeys') {
				await handlePasskeyAuthentication();
			} else if (providerData.provider === 'password') {
				// form changed to show password, do nothing else
			} else {
				unreachable(providerData.provider);
			}
		} catch (error) {
			console.error(error);
			toast('Error determining login provider', { icon: <X /> });
		}
	};

	const handlePasskeyAuthentication = async () => {
		try {
			// Fetch authentication options
			const authOptionsRes = await fetch(
				`${import.meta.env.VITE_WEBSOCKET_URL}/passkeys-generate-authentication-options?email=${form.email}`,
			);
			const authOptions = await authOptionsRes.json();

			if (authOptions.error) {
				toast(authOptions.error, { icon: <X /> });
				return;
			}

			// Start WebAuthn authentication
			const startAuthResponse = await startAuthentication({
				optionsJSON: authOptions,
			});

			console.log('body to send', startAuthResponse);

			{
				const dummyForm = document.createElement('form');
				dummyForm.method = 'POST';
				dummyForm.action = `${import.meta.env.VITE_WEBSOCKET_URL}/auth/callback/credentials`;

				const cb = document.createElement('input');
				cb.type = 'hidden';
				cb.name = 'callbackUrl';
				cb.value = `${import.meta.env.VITE_BASE_URL}/profile`;
				dummyForm.appendChild(cb);

				const email = document.createElement('input');
				email.type = 'hidden';
				email.name = 'email';
				email.value = form.email;
				dummyForm.appendChild(email);

				const passkey = document.createElement('input');
				passkey.type = 'hidden';
				passkey.name = 'passkey';
				passkey.value = JSON.stringify(startAuthResponse);
				dummyForm.appendChild(passkey);

				document.body.appendChild(dummyForm);
				dummyForm.submit();
			}
		} catch (error) {
			console.error(error);
			toast('Passkey authentication error', { icon: <X /> });
		}
	};

	const handleCredentialsLogin = async () => {
		const res = await fetch(`${import.meta.env.VITE_WEBSOCKET_URL}/login`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(form),
		});
		const data = (await res.json()) as { success: boolean; profile?: UserProfile };

		afterLogin(data.profile);
	};

	// const handleOauthLogin = (provider: string) => {
	// 	const form = document.createElement('form');
	// 	form.method = 'POST';
	// 	form.action = `${import.meta.env.VITE_WEBSOCKET_URL}/auth/signin/${provider}`;

	// 	const cb = document.createElement('input');
	// 	cb.type = 'hidden';
	// 	cb.name = 'callbackUrl';
	// 	cb.value = `${import.meta.env.VITE_BASE_URL}/profile`;
	// 	form.appendChild(cb);

	// 	document.body.appendChild(form);
	// 	form.submit();
	// };

	// const oauthProviders = [
	// 	{ id: 'google', icon: <IconGoogle /> },
	// 	{ id: 'apple', icon: <IconApple /> },
	// 	{ id: 'microsoft-entra-id', icon: <IconMicrosoft /> },
	// ];

	return (
		<div className="flex flex-col gap-5">
			<Heading>Login</Heading>
			<FormProvider {...methods}>
				<form
					method="POST"
					action={`${import.meta.env.VITE_WEBSOCKET_URL}/auth/callback/credentials`}
					className="flex flex-col gap-5"
					onSubmit={
						provider === 'password'
							? (e) => {
									e.preventDefault();
									handleCredentialsLogin();
								}
							: (e) => {
									e.preventDefault();
									determineLoginProvider();
								}
					}
				>
					<div className="mb-3 mt-4 flex flex-col gap-3">
						<input type="hidden" name="callbackUrl" value={`${import.meta.env.VITE_BASE_URL}/login-result`} />
						<TextField
							label="email"
							type="email"
							placeholder="Email address"
							name="email"
							hideLabel
							autoFocus={!(provider === 'password')}
						/>
						{provider === 'password' && (
							<TextField
								label="password"
								type="password"
								placeholder="Password"
								name="password"
								hideLabel
								autoFocus={provider === 'password'}
							/>
						)}
					</div>
					<Button type="submit" className="w-full">
						{provider === 'password' ? 'Login' : 'Continue'}
					</Button>
				</form>
			</FormProvider>
			{/* <LoginOr /> */}
			{/* <Button mode="outline" className="w-full">
				Login with passkey
			</Button> */}
			{/* <Button mode="transparent" className="w-full" to="/forgot-password">
				Forgot password?
			</Button> */}
			{/* <div className="flex w-full flex-row place-content-center gap-3">
				{oauthProviders.map((provider) => (
					<Button key={provider.id} size="lg" mode="outline" onClick={() => handleOauthLogin(provider.id)}>
						{provider.icon}
					</Button>
				))}
			</div> */}
		</div>
	);
}

export default RouteComponent;
